import { Grid, Typography, Divider, Button, Box, Link } from '@mui/material';
import React, { useState } from 'react';
import { CustomerDTO, SiteDTO, SiteIntegrationTypeEnum } from '@beable/roster-backend-client-v1';
import CustomerAndSiteSelector from '../Common/CustomerAndSiteSelector';
import Header from '../Header/Header';
import FileUpload from './FileUpload';
import BatchTrigger from './BatchTrigger';
import BasePanel from '../Common/Layout/BasePanel';
import { BasePanelBox } from '../Common/Layout/BasePanelBox';
import BaseSheet from '../Common/Layout/BaseSheet';
import { StyledPanelHeader } from '../Common/Layout/PanelHeader';

import rosterBackendService from '../../services/RosterBackendService';
import moment from 'moment';
import BaseDialog from '../Common/BaseDialog';
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import { useNavigate } from "react-router-dom";

const initialCustomerValue: CustomerDTO = new CustomerDTO();
const initialSiteValue: SiteDTO = new SiteDTO();


export default function DataImport() {
  const [customer, setCustomer] = useState(initialCustomerValue);
  const [site, setSite] = useState(initialSiteValue);
  const [hasActivePauseDates, setHasActivePauseDates] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleCustomerChange = (customer: CustomerDTO) => {
    setCustomer(customer);
  };

  const handleSiteChange = async (site: SiteDTO) => {
    if (site) {
      await checkPauseDates(site.id);
      setSite(site);
      return;
    }
    setSite(initialSiteValue);
  }

  const checkPauseDates = async (siteId: number) => {

    const response = await rosterBackendService.getSitePauseDatesWithParse(siteId);

    if (response.isOk()) {
      const todayMoment = moment();
      if (response && response.value.pauseDates) {
        for (const pauseDate of response.value.pauseDates) {
          if (pauseDate.active) {
            if (todayMoment.isBetween(pauseDate.startDate.toString(), pauseDate.endDate.toString(), 'day', '[]')) {
              setHasActivePauseDates(true);
              setDialogOpen(true);
            }
          }
        }
      }
    } else {
      setHasActivePauseDates(false);
      setDialogOpen(false);
    }
  }

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const goToCustomerSetup = () => {
    navigate(`/customer-setup`);
  }

  const isOneRosterCSV = site?.integrationType === SiteIntegrationTypeEnum.OneRosterCSV;
  const introText = isOneRosterCSV
    ? 'Import CSV files.'
    : 'Trigger a roster run from your provider.';

  return (
    <>
      <Header />
      <Grid container>
        <BasePanel>
          <BasePanelBox>
            <StyledPanelHeader header="Import Data" />
            <Grid container size={12}>
              <CustomerAndSiteSelector onCustomerSelect={handleCustomerChange} onSiteSelect={handleSiteChange} />
            </Grid>
          </BasePanelBox>
        </BasePanel>
        <BaseSheet maxWidth="lg">
          {!hasActivePauseDates && site && site.integrationType === SiteIntegrationTypeEnum.OneRosterCSV ? (
            <>
              <BatchTrigger site={site} />
              <Divider sx={{ marginTop: '50px', marginBottom: '24px' }} />
              <Typography align-left='true' sx={{
                marginBottom: "16px"
              }}>{introText}</Typography>
              <Grid
                size={{
                  md: 12
                }}>
                <span data-cy='dragDropORContainer'>
                  <FileUpload
                    customerId={customer.id}
                    site={site}
                    prompt={"Drag and drop a One Roster zip file here, or click to select a file"}
                  />
                </span>
                <Divider sx={{ marginTop: '50px' }} />
                <span data-cy='dragDropSRContainer'>
                  <FileUpload
                    customerId={customer.id}
                    site={site}
                    prompt={"Drag and drop a Summer roster zip file and complete the batch for me"}
                    type="summerRoster" // the backend does different things based on the specified upload type
                  />
                </span>
              </Grid>
            </>
          ) : (
            <Typography align-left='true' sx={{
              marginBottom: "16px"
            }}>{introText}</Typography>
          )}

          {!hasActivePauseDates && site && (site.integrationType === SiteIntegrationTypeEnum.OneRosterAPI || site.integrationType === SiteIntegrationTypeEnum.CleverAPI) ? (
            <BatchTrigger site={site} />
          ) : null}
        </BaseSheet>
        <BaseDialog
          open={dialogOpen}
          header="Site pause dates"
          showCloseIcon={true}
          showCloseButton={false}
          onClose={() => closeDialog()}
        >
          <Box
            sx={{
              minHeight: 150,
              display: "flex",
              alignItems: "center"
            }}>
            <Box sx={{
              width: "100%"
            }}>
              <DialogContentText>
                Upload is disabled for site due to active pause dates
                <Link
                  onClick={() => {
                    goToCustomerSetup();
                  }}
                  component="button"
                  variant="body2"
                  sx={{ marginLeft: '4px' }}
                  underline="hover">
                  Go to Customer Setup
                </Link>
              </DialogContentText>
              <DialogActions>
                <Button
                  onClick={closeDialog}
                  variant="contained"
                  color="secondary"
                >
                  Close
                </Button>
              </DialogActions>
            </Box>
          </Box>
        </BaseDialog>
      </Grid >
    </>
  );
}
